import { ReactElement, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { T, TF } from "react-translator-component";
import { useSelector } from "react-redux";
import {
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useAppDispatch } from "../../redux/store";
import dayjs from "../../util/dayjs";
import { Dayjs } from "dayjs";
import apiServices from "../services/apiServices";
import LoadingModal from "../../components/LoadingModal";
import AdminEditPageFrame from "../components/AdminEditPageFrame";
import SelectPostType from "../components/createPost/SelectPostType";
import Category from "../components/createPost/Category";
import Group from "../components/createPost/Group";
import SelectImages from "../components/createPost/SelectImages";
import Title from "../components/createPost/Title";
import Details from "../components/createPost/Details";
import SelectAttachments from "../components/createPost/SelectAttachments";
import Tags from "../components/createPost/Tags";
import VoteSetting from "../components/createPost/VoteSetting";
import PostRefSuggestion from "../components/createPost/PostRefSuggestion";
import Radios from "../components/createPost/Radios";
import Section from "../components/editors/Section";
import SubmitButton from "../components/editors/SubmitButton";
import NoticeBanner from "../components/NoticeBanner";
import { colors } from "../../common/colors";
import {
  Errors, PrivateUser, Tag, VoteOption,
  initialVoteSettingErrors, MAX_VOTE_OPTIONS_COUNT, MIN_VOTE_OPTIONS_COUNT,
  isSamePrivateUser
} from "../util/createPost";
import { UserEntity } from "../util/editors";
import { initialErrors, reset, setErrors, setItem, setPrivateUsers, setVoteOptions } from "../redux/createOpinionReducer";
import { setErrorObj } from "../redux/errorReducer";

export default function CreatePost(): ReactElement {
  const today = useMemo(() => dayjs(), []);
  const voteDeadline: Dayjs = today.add(90, "days")

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { admin } = useSelector((state: any) => state.adminAuth);
  const post = useSelector((state: any) => state.createOpinion.values);

  const locationState = useLocation().state;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [postCategories, setPostCategories] = useState<any>([]);
  const [postGroups, setPostGroups] = useState<any>([]);
  const [agree, setAgree] = useState<boolean>(false);

  const [successMessage, setSuccessMessage] = useState<string>("");
  const [displayNotice, setDisplayNotice] = useState<boolean>(false)

  const displayNoticeTime = 3000

  const [suggestion, setSuggestion] = useState<any>();

  const getSuggestion = async () => {
    try {
      setIsLoading(true)
      const res = await apiServices.getSuggestion(locationState?.suggestionId);
      setSuggestion(res.suggestion);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
    }
  };

  const apiCalls = async () => {
    try {
      setIsLoading(true)

      const resCategories = await apiServices.getCategories()
      const categories = resCategories.categories.map((item: { id: any; name: any }) => {
        return {
          id: item.id,
          value: item.id,
          name: item.name,
        };
      });
      setPostCategories(categories)

      const resGroups = await apiServices.getAllGroups()
      const groups = resGroups.groups.map((item: { id: any; name: any; posts_count: any }) => {
        return {
          id: item.id,
          value: item.id,
          name: item.name,
        };
      });
      setPostGroups(groups)
    } catch (error: any) {
      dispatch(setErrorObj(error))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    apiCalls()

    if (locationState?.suggestionId) getSuggestion()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState])

  const validateForm = () => {
    const newErrors: Errors = Object.assign({}, JSON.parse(JSON.stringify(initialErrors))); // ディープコピー

    if (!post.title) newErrors.title = `${T("mng_post_title")}${T("error_enter")}`;
    if (!post.details) newErrors.details = `${T("mng_details")}${T("error_enter")}`;
    if (!post.category) newErrors.category = `${T("post_category")}${T("error_select")}`;
    if (!post.group) newErrors.group = `${T("group")}${T("error_select")}`;

    if (post.acceptVotes) {
      const voteOptions = post.voteSetting.voteOptions.filter((option: VoteOption) => option._destroy === 0)
      const voteSettingError = initialVoteSettingErrors(voteOptions.length)
      voteOptions.forEach((option: any, index: number) => {
        if (!option.optionText || option.optionText.trim() === "") {
          voteSettingError.voteOptions[index].optionText = `${T("post_vote_option")}${T("error_enter")}`;
        }
      })
      if (voteDeadline === undefined) voteSettingError.expiryDate = `${T("end_date")}${T("error_enter")}`;
      if (post.voteSetting.voteQuestion === "") voteSettingError.voteQuestion = `${T("post_vote_question")}${T("error_enter")}`;
      if (voteOptions.length < MIN_VOTE_OPTIONS_COUNT) voteSettingError.limitVoteOptions = TF(T("error_min_vote_option_count"), MIN_VOTE_OPTIONS_COUNT);
      if (voteOptions.length > MAX_VOTE_OPTIONS_COUNT) voteSettingError.limitVoteOptions = TF(T("error_max_vote_option_count"), MAX_VOTE_OPTIONS_COUNT);
      newErrors.voteSetting = voteSettingError
    }

    dispatch(setErrors(newErrors));

    return !hasFormError(newErrors);
  };

  const hasFormError = (obj: any): boolean => {

    return Object.values(obj).some((item: any) => {
      if (typeof item === "string") {
        return item !== ""
      } else {
        return hasFormError(item)
      }
    })
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      window.scrollTo(0, 0);
      return false
    };

    setIsLoading(true);

    let postPrivateUsersAttributes: any = null
    if (post.isPrivate) {
      postPrivateUsersAttributes = []
      if (!post.privateUsers.some((privateUser: PrivateUser) => isSamePrivateUser(privateUser, admin.class_name, admin.id))) {
        postPrivateUsersAttributes.push({
          privater_id: admin.id,
          privater_type: admin.class_name,
        })
      }

      post.privateUsers.forEach((privateUser: PrivateUser) => {
        postPrivateUsersAttributes.push({
          id: privateUser.id,
          privater_id: privateUser.userId,
          privater_type: privateUser.userType,
          _destroy: privateUser._destroy
        })
      })
    }

    const voteOptionsAttributes = post.voteSetting.voteOptions.flatMap((option : VoteOption) => {
      if (!post.acceptVotes && !option.id) return []

      return [{
        id: option.id,
        option_text: option.optionText,
        _destroy: !post.acceptVotes ? 1 : option._destroy,
      }]
    });

    const tagAttributes = post.tags.map((tag: Tag) => {
      return {
        id: tag.id,
        tag: tag.tag,
        _destroy: tag._destroy === "1" ? 1 : null,
      }
    })

    const body = {
      post: {
        is_private: post.isPrivate,
        post_private_users_attributes: postPrivateUsersAttributes,
        title: post.title,
        details: post.details,
        group_id: Number(post.group),
        category_id: Number(post.category),
        expiry_date: post.acceptVotes ? dayjs(post.voteSetting.expiryDate)?.format("YYYY-M-D") : "",
        is_anonymous: post.showNameOption,
        enable_comments: post.acceptComments,
        enable_participants: post.acceptVotes,
        vote_options_attributes: voteOptionsAttributes,
        vote_question: post.voteSetting.voteQuestion,
        images_attributes: post.images,
        attachments_attributes: post.attachments,
        tags_attributes: tagAttributes,
      }
    };

    try {
      const res = post?.id ?
        await apiServices.editPost(post.id, body) :
        await apiServices.createPost(body);
      handleSuccess(res)
      return true
    } catch (error: any) {
      dispatch(setErrorObj(error))
      if (error?.response?.data?.error_key?.includes("already_voted")) {
        recyclePostData()
      }
      return false
    } finally {
      setIsLoading(false);
    }
  };

  const recyclePostData = async () => {
    if (!post?.id) return

    // 投票に変更があった場合、元に戻す
    try {
      setIsLoading(true);
      const res = await apiServices.getPost(post.id)
      const resVoteOption = res.post.vote_options
      const newVoteOptions: VoteOption[] = Object.assign([], JSON.parse(JSON.stringify(post.voteSetting.voteOptions)));

      resVoteOption.forEach((resOption: any) => {
        const voteOption = {
          id: resOption.id,
          optionText: resOption.option_text,
          isVoted: true,
          key: crypto.randomUUID(),
          _destroy: 0,
        }

        // 投票設定は変えていない時
        if (post.acceptVotes) {
          const index = newVoteOptions.findIndex((option: VoteOption) => option?.id === resOption.id)
          if (index >= 0) {
            newVoteOptions[index] = voteOption
          } else {
            newVoteOptions.push(voteOption)
          }
        } else  {
          if (newVoteOptions.some((option: VoteOption) => option?.id === resOption.id)) {
            const index = newVoteOptions.findIndex((option: VoteOption) => option?.id === resOption.id)
            newVoteOptions[index] = voteOption
          } else {
            newVoteOptions.push(voteOption)
          }
        }
      })

      newVoteOptions.sort((a: VoteOption, b: VoteOption) => {
        if (a?.id && b?.id) {
          return a.id - b.id
        } else if (a?.id && !b?.id) {
          return -1
        } else {
          return 1
        }
      })

      dispatch(setItem({key: 'acceptVotes', value: true}))
      dispatch(setVoteOptions({value: newVoteOptions}))
    } catch (error) {
      // pass
    } finally {
      setIsLoading(false);
    }
  }

  const handleSuccess = (res: any) => {
    if (!res?.data?.success) return

    const isApprovedPost = res?.data?.post?.status === "Approved" || res?.post?.status === "Approved"

    const desc = T(isApprovedPost ? "post_save_success" : "include_ng_words")
    setSuccessMessage(desc);
    setDisplayNotice(true)

    setTimeout(() => {
      navigate("/mng/navigator/navigator_posts");
      dispatch(reset())
    }, displayNoticeTime);
  }

  const handleAddPrivateUser = (user: UserEntity) => {
    const { userType, userId } = user;
    if (!userType) return;

    let newPrivateUsers: PrivateUser[] = Object.assign([], JSON.parse(JSON.stringify(post.privateUsers)));
    const isExists = post.privateUsers.some(
      (privateUser: PrivateUser) => isSamePrivateUser(privateUser, userType, userId)
    )

    if (isExists) {
      newPrivateUsers = newPrivateUsers.map((privateUser: PrivateUser) => {
        if (isSamePrivateUser(privateUser, userType, userId)) privateUser._destroy = 0
        return privateUser
      })
    } else {
      newPrivateUsers.push({
        userId: userId,
        name: user.name,
        nickname: user.nickname,
        image: user.image ?? "",
        userType: userType,
        _destroy: 0,
        identifier: crypto.randomUUID(),
      })
    }
    dispatch(setPrivateUsers({value: newPrivateUsers}))
  }

  const handleRemovePrivateUser = (user: UserEntity) => {
    const { userType, userId, identifier } = user;
    if (!userType || !identifier) return;

    if (userType === admin.class_name && userId === admin.id) return

    const targetMember = post.privateUsers.find((privateUser: PrivateUser) => privateUser.identifier === identifier)

    let newPrivateUsers: PrivateUser[] = Object.assign([], JSON.parse(JSON.stringify(post.privateUsers)));

    if (targetMember?.id) {
      newPrivateUsers = newPrivateUsers.map((member: PrivateUser) => {
        if (member.identifier === user.identifier) member._destroy = 1
        return member
      });
    } else {
      newPrivateUsers = newPrivateUsers.filter((member: PrivateUser) => member.identifier !== user.identifier);
    }
    dispatch(setPrivateUsers({value: newPrivateUsers}))
  }

  return (
    <>
      {suggestion && <PostRefSuggestion suggestion={suggestion} />}

      <AdminEditPageFrame sx={{ width: suggestion ? "70%" : "100%" }}>
        <LoadingModal visible={isLoading} />

        <div style={styles.formBackground}>
          <div style={ styles.selectUsersContainer } >
            <SelectPostType
              isEdit={post?.id ? true : false}
              handleAddPrivateUser={handleAddPrivateUser}
              handleRemovePrivateUser={handleRemovePrivateUser}
            />
          </div>

          <Category categories={postCategories}/>

          <Group groups={postGroups}/>

          <SelectImages />

          <Title />

          <Details />

          <SelectAttachments />

          <Tags />

          <VoteSetting
            minimum={MIN_VOTE_OPTIONS_COUNT}
            maximum={MAX_VOTE_OPTIONS_COUNT}
            baseDate={today}
            limitDate={voteDeadline}
          />

          <div style={{ marginTop: 40 }}>
            <Section
              title={`${T("select_accept_comments")}${T("optional")}`}
              body={
                <Radios
                  value={post.acceptComments}
                  options={[
                    { value: true, label: T("yes") },
                    { value: false, label: T("no") },
                  ]}
                  onChange={(value) => dispatch(setItem({key: 'acceptComments', value: value === "true"}))}
                />
              }
            />
          </div>

          <div style={{ marginTop: 40 }}>
            <Section
              title={
                <>
                  {T("select_post_name")}
                  <span style={ styles.colerRed }> *&nbsp;</span><br/>
                  <span style={ styles.colerGray200 }>{T("post_name_description")}</span>
                </>
              }
              body={
                <Radios
                  value={post.showNameOption}
                  options={[
                    { value: 1, label: T("nickname") },
                    { value: 2, label: T("real_name")},
                    { value: 3, label: T("anonymous") },
                  ]}
                  onChange={(value) => dispatch(setItem({key: 'showNameOption', value: Number(value)}))}
                />
              }
            />
          </div>
        </div>

        <div style={ styles.noticeContainer }>
          <Section
            title={`${T("notice")}${T("required")}`}
            body={
              <>
                <Typography style={ styles.noticeLabel }>{T("post_notes_1")}</Typography>
                <Typography style={ styles.noticeLabel }>{T("post_notes_2")}</Typography>
                <Typography style={ styles.noticeLabel }>{T("post_notes_3")}</Typography>

                <FormControlLabel
                  control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
                  color="error"
                  sx={ styles.agreementLabel }
                  label={T("agreement_post_notice")}
                />
              </>
            }
          />
        </div>

        {/* Submit Button */}
        <SubmitButton
          title={T("submit_post")}
          validate={async () => agree}
          onSubmit={handleSubmit}
        />

        <NoticeBanner
          open={displayNotice}
          messageList={[successMessage]}
          type={"success"}
          style={styles.w95}
          onClose={() => setDisplayNotice(false)}
        />

      </AdminEditPageFrame>
    </>
  );
}

const styles = {
  formBackground: {
    backgroundColor: colors.farm_gray15,
    padding: "15px 20px",
  },

  w95: {
    width: "95%",
  },

  colerRed: {
    color: colors.farm_red600
  },

  colerGray200: {
    color: colors.farm_gray200
  },

  noticeContainer: {
    padding: "15px 10px",
  },

  noticeLabel: {
    borderBottomColor: colors.farm_gray100,
    borderBottomWidth: 1,
    borderBottomStyle: "solid" as const,
    fontSize: 14,
    marginBottom: 6,
    marginTop: 10
  },

  agreementLabel: {
    color: colors.farm_black,
    marginTop: "30px",
  },

  selectUsersContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
};
